import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  TruckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import ReservationDetailDrawer from '../../components/reservations/ReservationDetailDrawer';

type DispatchReservation = {
  id: string;
  farmOut?: { id: string; state: string; recipientName: string | null; attentionNeeded: boolean } | null;
  reservationRef?: string | null;
  lifecycleLabel?: string | null;
  localStartTime?: string | null;
  localEndTime?: string | null;
  timezone?: string | null;
  customerName?: string | null;
  accountName?: string | null;
  pickup?: string | null;
  dropoff?: string | null;
  assignmentStatus?: string | null;
  driverAssignmentStatus?: string | null;
  paymentStatus?: string | null;
  dispatchStatus?: string | null;
  dispatchChecklist?: {
    customerConfirmed?: boolean;
    routeReviewed?: boolean;
    vehicleReady?: boolean;
    driverConfirmed?: boolean;
    finalOpsApproved?: boolean;
  };
  dispatchWarnings?: string[];
  vehicleSummary?: {
    assigned?: number;
    required?: number;
    labels?: string[];
  };
  driverSummary?: {
    assigned?: number;
    required?: number;
    labels?: string[];
  };
};

type DispatchGroup = {
  key: string;
  label: string;
  count: number;
  reservations: DispatchReservation[];
};

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function formatLabel(value?: string | null) {
  const normalized = String(value || '').trim();
  if (!normalized) return 'N/A';
  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function statusPillClass(value?: string | null) {
  const normalized = String(value || '').toLowerCase();
  if (normalized.includes('due') || normalized.includes('pending') || normalized.includes('unassigned')) {
    return 'bg-amber-100 text-amber-800';
  }
  if (normalized.includes('partial')) return 'bg-sky-100 text-sky-800';
  if (normalized.includes('assigned') || normalized.includes('paid') || normalized.includes('ready')) {
    return 'bg-green-100 text-green-800';
  }
  if (normalized.includes('cancel')) return 'bg-red-100 text-red-800';
  return 'bg-surface-container-high text-on-surface-variant';
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
}) {
  return (
    <div className="admin-subpanel rounded-card p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-on-surface-variant">{label}</p>
        <span className="rounded-lg bg-surface-container-high p-2 text-on-surface-variant">
          {icon}
        </span>
      </div>
      <p className="mt-2 text-2xl font-semibold text-on-surface">{value}</p>
    </div>
  );
}

export default function AdminDispatch() {
  const [date, setDate] = useState(todayKey());
  const [rangeDays, setRangeDays] = useState(1);
  const [selectedReservationId, setSelectedReservationId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const dispatchQuery = useQuery(
    ['admin-dispatch', date, rangeDays],
    async () => {
      const response = await adminAPI.getDispatch({ date, rangeDays });
      return response.data;
    },
    { keepPreviousData: true },
  );

  const groups = useMemo<DispatchGroup[]>(() => {
    return Array.isArray(dispatchQuery.data?.groups) ? dispatchQuery.data.groups : [];
  }, [dispatchQuery.data?.groups]);
  const summary = dispatchQuery.data?.summary || {};
  const manifests = dispatchQuery.data?.manifests || { drivers: [], vehicles: [] };
  const visibleReservations = groups.reduce((sum, group) => sum + (group.count || 0), 0);
  const dispatchActionMutation = useMutation(
    async ({ reservationId, action }: { reservationId: string; action: string }) => {
      const response = await adminAPI.dispatchReservationAction(reservationId, { action });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Dispatch updated');
        queryClient.invalidateQueries('admin-dispatch');
        queryClient.invalidateQueries('admin-reservations');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || error?.message || 'Unable to update dispatch');
      },
    },
  );
  const runDispatchAction = (
    event: React.MouseEvent<HTMLButtonElement>,
    reservationId: string,
    action: string,
  ) => {
    event.stopPropagation();
    dispatchActionMutation.mutate({ reservationId, action });
  };

  return (
    <PageLayout className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-on-surface">Dispatch</h1>
          <p className="mt-1 text-on-surface-variant">
            Coordinate deposits, vehicle assignment, and driver assignment for upcoming service.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-on-surface-variant">Date</span>
            <input
              type="date"
              className="input-field min-w-[160px]"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-on-surface-variant">Range</span>
            <select
              className="input-field min-w-[120px]"
              value={rangeDays}
              onChange={(event) => setRangeDays(Number(event.target.value))}
            >
              <option value={1}>1 day</option>
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
            </select>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
        <StatCard label="On Board" value={visibleReservations} icon={<CalendarDaysIcon className="h-5 w-5" />} />
        <StatCard label="Pending Deposit" value={summary.pendingDeposit || 0} icon={<CreditCardIcon className="h-5 w-5" />} />
        <StatCard label="Needs Vehicle" value={summary.needsVehicle || 0} icon={<TruckIcon className="h-5 w-5" />} />
        <StatCard label="Needs Driver" value={summary.needsDriver || 0} icon={<UserIcon className="h-5 w-5" />} />
        <StatCard label="Ready / Active" value={(summary.assignedDrivers || 0) + (summary.todayOrActive || 0)} icon={<CheckCircleIcon className="h-5 w-5" />} />
      </div>

      <section className="admin-subpanel rounded-card p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-on-surface">Dispatch Board</h2>
            <p className="mt-1 text-sm text-on-surface-variant">
              Click a row to open the reservation drawer for billing, vehicle, and driver actions.
            </p>
          </div>
          {dispatchQuery.isFetching ? (
            <span className="rounded-pill bg-surface-container-high px-3 py-1 text-xs font-medium text-on-surface-variant">
              Refreshing...
            </span>
          ) : null}
        </div>

        {dispatchQuery.isLoading ? (
          <LoadingSpinner className="py-20" />
        ) : groups.length ? (
          <div className="space-y-5">
            {groups.map((group) => (
              <div key={group.key} className="rounded-card border border-outline/60 bg-surface-container-lowest">
                <div className="flex items-center justify-between border-b border-outline/60 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <ClockIcon className="h-4 w-4 text-on-surface-variant" />
                    <h3 className="font-semibold text-on-surface">{group.label}</h3>
                  </div>
                  <span className="rounded-pill bg-surface-container-high px-3 py-1 text-xs font-medium text-on-surface-variant">
                    {group.count} {group.count === 1 ? 'reservation' : 'reservations'}
                  </span>
                </div>
                {group.reservations.length ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-outline">
                      <thead className="bg-surface-container-low">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-on-surface-variant">Reservation</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-on-surface-variant">Customer</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-on-surface-variant">Route</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-on-surface-variant">Vehicles</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-on-surface-variant">Drivers</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-on-surface-variant">Payment</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-on-surface-variant">Dispatch</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-outline bg-surface-container-lowest">
                        {group.reservations.map((reservation) => (
                          <tr
                            key={`${group.key}-${reservation.id}`}
                            className="cursor-pointer hover:bg-surface-container-low"
                            onClick={() => setSelectedReservationId(reservation.id)}
                            tabIndex={0}
                            role="button"
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setSelectedReservationId(reservation.id);
                              }
                            }}
                          >
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm font-semibold text-on-surface">
                                {reservation.reservationRef || reservation.id.slice(0, 8)}
                              </div>
                              <div className="mt-1 text-xs text-on-surface-variant">
                                {reservation.localStartTime || 'No start time'} to {reservation.localEndTime || 'N/A'}
                              </div>
                              <span className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusPillClass(reservation.lifecycleLabel)}`}>
                                {reservation.farmOut ? (reservation.farmOut.state === 'open' ? 'Open farm-out offer' : `Farmed out to ${reservation.farmOut.recipientName}`) : reservation.lifecycleLabel || 'Scheduled'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm font-medium text-on-surface">{reservation.customerName || 'Unknown customer'}</div>
                              <div className="text-xs text-on-surface-variant">{reservation.accountName || 'No account'}</div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="max-w-[360px] text-sm text-on-surface">
                                {reservation.pickup || 'Pickup not set'}
                              </div>
                              <div className="max-w-[360px] truncate text-xs text-on-surface-variant">
                                to {reservation.dropoff || 'Dropoff not set'}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm font-medium text-on-surface">
                                {reservation.vehicleSummary?.assigned || 0}/{reservation.vehicleSummary?.required || 0} assigned
                              </div>
                              <div className="max-w-[220px] truncate text-xs text-on-surface-variant">
                                {reservation.vehicleSummary?.labels?.join(', ') || formatLabel(reservation.assignmentStatus)}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm font-medium text-on-surface">
                                {reservation.driverSummary?.assigned || 0}/{reservation.driverSummary?.required || 0} assigned
                              </div>
                              <div className="max-w-[220px] truncate text-xs text-on-surface-variant">
                                {reservation.driverSummary?.labels?.join(', ') || formatLabel(reservation.driverAssignmentStatus)}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusPillClass(reservation.paymentStatus)}`}>
                                {formatLabel(reservation.paymentStatus)}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <span className={`mb-2 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusPillClass(reservation.dispatchStatus)}`}>
                                {formatLabel(reservation.dispatchStatus)}
                              </span>
                              <div className="flex min-w-[260px] flex-wrap gap-1.5">
                                {!reservation.dispatchChecklist?.customerConfirmed ? (
                                  <button
                                    type="button"
                                    className="btn-secondary px-2 py-1 text-xs"
                                    disabled={dispatchActionMutation.isLoading}
                                    onClick={(event) => runDispatchAction(event, reservation.id, 'confirm_customer')}
                                  >
                                    Confirm customer
                                  </button>
                                ) : null}
                                {!reservation.dispatchChecklist?.routeReviewed ? (
                                  <button
                                    type="button"
                                    className="btn-secondary px-2 py-1 text-xs"
                                    disabled={dispatchActionMutation.isLoading}
                                    onClick={(event) => runDispatchAction(event, reservation.id, 'review_route')}
                                  >
                                    Route reviewed
                                  </button>
                                ) : null}
                                {reservation.assignmentStatus === 'assigned' && !reservation.dispatchChecklist?.vehicleReady ? (
                                  <button
                                    type="button"
                                    className="btn-secondary px-2 py-1 text-xs"
                                    disabled={dispatchActionMutation.isLoading}
                                    onClick={(event) => runDispatchAction(event, reservation.id, 'mark_vehicle_ready')}
                                  >
                                    Vehicle ready
                                  </button>
                                ) : null}
                                {reservation.driverAssignmentStatus === 'assigned' && !reservation.dispatchChecklist?.driverConfirmed ? (
                                  <button
                                    type="button"
                                    className="btn-secondary px-2 py-1 text-xs"
                                    disabled={dispatchActionMutation.isLoading}
                                    onClick={(event) => runDispatchAction(event, reservation.id, 'confirm_driver')}
                                  >
                                    Driver confirmed
                                  </button>
                                ) : null}
                                {group.key === 'route_incomplete' ? (
                                  <button
                                    type="button"
                                    className="btn-primary px-2 py-1 text-xs"
                                    disabled={dispatchActionMutation.isLoading}
                                    onClick={(event) => runDispatchAction(event, reservation.id, 'mark_ready')}
                                  >
                                    Approve
                                  </button>
                                ) : null}
                                {group.key === 'ready' ? (
                                  <button
                                    type="button"
                                    className="btn-primary px-2 py-1 text-xs"
                                    disabled={dispatchActionMutation.isLoading}
                                    onClick={(event) => runDispatchAction(event, reservation.id, 'start')}
                                  >
                                    Start
                                  </button>
                                ) : null}
                                {String(reservation.dispatchStatus || '') === 'in_progress' ? (
                                  <button
                                    type="button"
                                    className="btn-primary px-2 py-1 text-xs"
                                    disabled={dispatchActionMutation.isLoading}
                                    onClick={(event) => runDispatchAction(event, reservation.id, 'complete')}
                                  >
                                    Complete
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  className="btn-secondary px-2 py-1 text-xs"
                                  disabled={dispatchActionMutation.isLoading}
                                  onClick={(event) => runDispatchAction(event, reservation.id, 'attention_needed')}
                                >
                                  Attention
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-4 py-6 text-sm text-on-surface-variant">
                    No reservations in this state for the selected range.
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-16 text-center">
            <CalendarDaysIcon className="mx-auto h-12 w-12 text-on-surface-variant" />
            <h3 className="mt-4 text-lg font-semibold text-on-surface">No dispatch items</h3>
            <p className="mt-2 text-sm text-on-surface-variant">
              No reservations are scheduled for this date range.
            </p>
          </div>
        )}
      </section>

      {(manifests.drivers?.length || manifests.vehicles?.length) ? (
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="admin-subpanel rounded-card p-4">
            <h2 className="text-lg font-semibold text-on-surface">Driver Manifest</h2>
            <div className="mt-3 divide-y divide-outline">
              {(manifests.drivers || []).slice(0, 12).map((item: any) => (
                <button
                  key={`driver-${item.driverName}-${item.reservationId}`}
                  type="button"
                  className="block w-full py-3 text-left hover:bg-surface-container-low"
                  onClick={() => setSelectedReservationId(item.reservationId)}
                >
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-semibold text-on-surface">{item.driverName}</span>
                    <span className="text-on-surface-variant">{item.startTime || 'No time'}</span>
                  </div>
                  <p className="mt-1 truncate text-xs text-on-surface-variant">
                    {item.reservationRef || item.reservationId.slice(0, 8)} · {item.customerName || 'Unknown customer'}
                  </p>
                </button>
              ))}
            </div>
          </div>
          <div className="admin-subpanel rounded-card p-4">
            <h2 className="text-lg font-semibold text-on-surface">Vehicle Manifest</h2>
            <div className="mt-3 divide-y divide-outline">
              {(manifests.vehicles || []).slice(0, 12).map((item: any) => (
                <button
                  key={`vehicle-${item.vehicleLabel}-${item.reservationId}`}
                  type="button"
                  className="block w-full py-3 text-left hover:bg-surface-container-low"
                  onClick={() => setSelectedReservationId(item.reservationId)}
                >
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-semibold text-on-surface">{item.vehicleLabel}</span>
                    <span className="text-on-surface-variant">{item.startTime || 'No time'}</span>
                  </div>
                  <p className="mt-1 truncate text-xs text-on-surface-variant">
                    {item.reservationRef || item.reservationId.slice(0, 8)} · {(item.driverLabels || []).join(', ') || 'No driver'}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <ReservationDetailDrawer
        open={Boolean(selectedReservationId)}
        onClose={() => setSelectedReservationId(null)}
        reservationId={selectedReservationId}
        onReservationUpdated={() => {
          queryClient.invalidateQueries('admin-dispatch');
          queryClient.invalidateQueries('admin-reservations');
        }}
      />
    </PageLayout>
  );
}
